import { useEffect as useEffectN, useRef as useRefN, useState as useStateN } from "react";
import { Icon } from "./components";

// Notifications bell — top-bar dropdown of recent in-app notifications.
// Polls the unread count on the badge; loads the list when opened.

const TYPE_ICON = {
  message_received: "chat",
  event_posted: "calendar",
  trial_expiring: "clock",
  stripe_trial_ending: "clock",
  subscription_activated: "check",
  subscription_changed: "coin",
  subscription_canceled: "x",
  renewal_upcoming: "clock",
  payment_received: "coin",
  payment_failed: "shield",
  card_expiring: "shield",
  generic: "bell",
};

function _relTimeN(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// `onUserUpdate` is App.jsx's `setUser` (a React useState setter) —
// callers may invoke it with EITHER a fresh user object (`setUser(u)`,
// see `settings.jsx`) OR with a functional updater (`setUser(cur =>
// patched)`, used here so the poll interval and optimistic-bump
// helpers don't capture a stale `user` closure). The polymorphism is
// React's, not ours; documented per CODE_REVIEW P2 L290.
function NotificationsBell({ user, onUserUpdate, onAction }) {
  const [open, setOpen] = useStateN(false);
  const [items, setItems] = useStateN(null);
  const [loading, setLoading] = useStateN(false);
  const rootRef = useRefN(null);

  const unread = user?.unread_notifications_count || 0;

  // Close on outside click + Escape
  useEffectN(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Poll unread count every 30s + on tab focus
  useEffectN(() => {
    if (!user) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const { count } = await window.Api.notificationsUnreadCount();
        if (cancelled) return;
        if (onUserUpdate) {
          // Tiny patch — only update the field that changed.
          onUserUpdate((cur) => (cur && cur.unread_notifications_count !== count
            ? { ...cur, unread_notifications_count: count }
            : cur));
        }
      } catch (_) { /* ignore */ }
    };
    refresh();
    const i = setInterval(refresh, 30_000);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(i);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line
  }, [user?.id]);

  const loadList = async () => {
    setLoading(true);
    try {
      const list = await window.Api.notificationsList();
      setItems(list || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const optimisticBumpUnread = (delta) => {
    if (!onUserUpdate) return;
    onUserUpdate((cur) => {
      if (!cur) return cur;
      const next = Math.max(0, (cur.unread_notifications_count || 0) + delta);
      return { ...cur, unread_notifications_count: next };
    });
  };

  const onPick = async (n) => {
    setOpen(false);
    if (n.unread) {
      try {
        await window.Api.notificationsMarkRead(n.id);
        optimisticBumpUnread(-1);
      } catch (_) { /* ignore */ }
    }
    if (onAction && n.action_route) {
      onAction(n.action_route, n.action_payload || null);
    }
  };

  const onMarkAll = async () => {
    try {
      await window.Api.notificationsMarkAllRead();
      setItems((cur) => (cur || []).map((n) => ({ ...n, unread: false, read_at: new Date().toISOString() })));
      if (onUserUpdate) {
        onUserUpdate((cur) => cur ? { ...cur, unread_notifications_count: 0 } : cur);
      }
    } catch (_) { /* ignore */ }
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        className="btn btn--ghost btn--sm"
        title="Notifications"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span style={{
          position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon name="bell" size={16} />
          {unread > 0 && (
            <span style={{
              position: "absolute", top: -5, right: -7,
              minWidth: 16, height: 16, padding: "0 4px", borderRadius: 10,
              background: "var(--accent)", color: "var(--surface)",
              fontSize: 10, fontWeight: 600, lineHeight: "16px",
              border: "1.5px solid var(--bg)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            width: "min(380px, calc(100vw - 24px))",
            maxHeight: "70vh",
            background: "var(--surface)",
            border: "1px solid var(--hairline)",
            borderRadius: 12,
            boxShadow: "var(--shadow-lg)",
            zIndex: 60,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div className="row" style={{ padding: "10px 14px", borderBottom: "1px solid var(--hairline)", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 600 }}>Notifications</div>
            {unread > 0 && (
              <button className="btn btn--ghost btn--sm" onClick={onMarkAll} style={{ padding: "2px 8px" }}>
                Mark all read
              </button>
            )}
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && <div style={{ padding: 18, color: "var(--mute)", fontSize: 13 }}>Loading…</div>}
            {!loading && items && items.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--mute)", fontSize: 13 }}>
                You're all caught up.
              </div>
            )}
            {!loading && items && items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => onPick(n)}
                style={{
                  display: "flex", gap: 10, width: "100%", alignItems: "flex-start",
                  padding: "12px 14px",
                  borderBottom: "1px solid var(--hairline)",
                  background: n.unread ? "color-mix(in oklab, var(--accent) 6%, var(--surface))" : "var(--surface)",
                  border: 0, textAlign: "left", cursor: n.action_route ? "pointer" : "default",
                  borderBottomColor: "var(--hairline)",
                  borderBottomStyle: "solid", borderBottomWidth: 1,
                }}
              >
                <span style={{
                  flexShrink: 0, width: 28, height: 28, borderRadius: 8,
                  background: "var(--bg-2)", color: "var(--mute)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icon name={TYPE_ICON[n.type] || "bell"} size={14} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: n.unread ? 600 : 500, fontSize: 13.5, lineHeight: 1.3 }}>
                    {n.title}
                  </div>
                  {n.body && (
                    <div style={{ fontSize: 12.5, color: "var(--mute)", marginTop: 2, lineHeight: 1.4 }}>
                      {n.body}
                    </div>
                  )}
                  <div className="text-xs text-mute" style={{ marginTop: 4 }}>{_relTimeN(n.updated_at)}</div>
                </div>
                {n.unread && (
                  <span style={{
                    flexShrink: 0, width: 8, height: 8, borderRadius: "50%",
                    background: "var(--accent)", marginTop: 6,
                  }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { NotificationsBell };
